import { OfficeIcon } from "components/icons/v2/Office";
import { US } from "constants/countryCodes";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { pluralize } from "utils/pluralize";
import {
  EntityReviewItem,
  useGetGroupReviewDetailsQuery,
} from "v2/store/onboardingEntityApi";
import {
  hasHomeState,
  hasIncorporationDate,
} from "v2/utils/isEntityReviewComplete";
import { useOnboardingData } from "../../OnboardingDataContext";
import { EntityReviewAccordion } from "./EntityReviewAccordion";
import { ReviewLoader } from "./ReviewLoader";

type ReviewEntityStepProps = {
  onComplete: (summary: ReactNode) => void;
  showErrors?: boolean;
};

const REVIEW_POLL_INTERVAL_MS = 3000;
const REVIEW_POLL_OFF = 0;

const MISSING_DATE_LABEL = "date of incorporation";
const MISSING_STATE_LABEL = "state of incorporation";

export const getReviewMandatoryError = (
  reviewData: EntityReviewItem[]
): string | null => {
  const entities = reviewData.filter((item) => item.countryCode === US);
  const isDateMissing = entities.some(
    (entity) => !hasIncorporationDate(entity)
  );
  const isStateMissing = entities.some((entity) => !hasHomeState(entity));

  const missingLabels = [
    isDateMissing ? MISSING_DATE_LABEL : null,
    isStateMissing ? MISSING_STATE_LABEL : null,
  ].filter(Boolean);

  if (missingLabels.length === 0) return null;

  return `Add ${missingLabels.join(" and ")} for all entities to continue`;
};

export const buildReviewEntitySummary = (
  companyName: string,
  items: EntityReviewItem[]
): ReactNode => {
  const parts = [companyName];
  if (items.length > 0) {
    parts.push(pluralize(items.length, "Entity", "Entities"));
  }
  const peopleCount = items.reduce((sum, item) => sum + item.people.length, 0);
  if (peopleCount > 0) {
    parts.push(pluralize(peopleCount, "Persona", "Personas"));
  }
  return (
    <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60">
      <span className="t-flex t-shrink-0 t-rounded t-bg-surface-purple t-p-1 t-text-purple-70">
        <OfficeIcon />
      </span>
      <span>{parts.join(" · ")}</span>
    </div>
  );
};

export const ReviewEntityStep = ({
  onComplete: _onComplete,
  showErrors = false,
}: ReviewEntityStepProps) => {
  const { data } = useOnboardingData();

  const [pollInterval, setPollInterval] = useState(REVIEW_POLL_INTERVAL_MS);
  const {
    data: reviewData,
    isLoading,
    isFetching,
  } = useGetGroupReviewDetailsQuery(
    { groupId: data.groupId },
    {
      skip: !data.groupId || data.entitiesUpdating,
      pollingInterval: pollInterval,
      refetchOnMountOrArgChange: true,
    }
  );

  const [isReady, setIsReady] = useState(false);

  const usReviewData = useMemo(
    () => reviewData?.filter((item) => item.countryCode === US) ?? [],
    [reviewData]
  );

  const isVerifying =
    isFetching ||
    !reviewData ||
    reviewData.length === 0 ||
    usReviewData.some((entity) => entity.verificationPending);

  useEffect(() => {
    if (isVerifying || isReady) return;

    setPollInterval(REVIEW_POLL_OFF);
    setIsReady(true);
  }, [isVerifying, isReady]);

  if (isLoading || !isReady) {
    return <ReviewLoader />;
  }

  return (
    <div className="t-flex t-flex-col">
      <p className="t-m-0 t-px-0 t-pb-5 t-text-body-sm t-text-text-30">
        Review any available information and add anything that's missing.
      </p>

      <div className="t-flex t-flex-col t-gap-5">
        {usReviewData.map((item, index) => (
          <div
            key={item.entityId}
            className="t-border-0 t-border-b t-border-solid t-border-neutral-0 t-pb-5 last:t-border-0 last:t-pb-0"
          >
            <EntityReviewAccordion
              item={item}
              groupId={data.groupId}
              countryCode={item.countryCode || US}
              defaultOpen={index === 0}
              showErrors={showErrors}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
